// Utilities to generate/apply simple TXT translations for a survey language
// TXT format:
// Q1: <questionContent>
//  - <option label>   (SC/MC/Open/SelectText/OpenList extras)
//  + <dropdown label> (OpenList dropdown options)
// Q2: <questionContent>
//  ...

function normalizeQuestionsByPage(questions = []) {
    const arr = Array.isArray(questions) ? questions.slice() : [];
    const withIdx = arr.map((q, idx) => ({ q, idx }));
    withIdx.sort((a, b) => {
        const ap = Number(a.q?.pageNumber || 1);
        const bp = Number(b.q?.pageNumber || 1);
        if (ap !== bp) return ap - bp;
        return a.idx - b.idx;
    });
    return withIdx.map((x) => x.q);
}

export function generateTxtForSurveyLanguage(survey, lang, { includeOptions = true } = {}) {
    if (!survey || !lang) return '';
    const section = survey?.[lang] || {};
    const questions = normalizeQuestionsByPage(section.questionnaire || []);
    const lines = [];
    questions.forEach((q, i) => {
        const title = (q?.questionContent ?? '').toString();
        lines.push(`Q${i + 1}: ${title}`);
        if (includeOptions) {
            const t = q?.questionType;
            const opts = Array.isArray(q?.details?.options) ? q.details.options : [];
            const selectOpts = Array.isArray(q?.details?.selectOptions)
                ? q.details.selectOptions
                : [];

            const getLabel = (opt) => (typeof opt === 'object' && opt !== null ? (opt.label ?? '') : String(opt ?? ''));

            if ((t === 'SC' || t === 'MC' || t === 'Open' || t === 'SelectText' || t === 'OpenList') && opts.length) {
                opts.forEach((o) => lines.push(` - ${getLabel(o)}`));
            }
            if (t === 'OpenList' && selectOpts.length) {
                selectOpts.forEach((o) => lines.push(` + ${getLabel(o)}`));
            }
        }
    });
    return lines.join('\n');
}

export function applyTxtTranslationToSurvey(survey, lang, txt) {
    const section = survey?.[lang] || {};
    const original = normalizeQuestionsByPage(section.questionnaire || []);
    const lines = (txt || '').replace(/\r\n/g, '\n').split('\n');
    const qBlocks = [];
    let cur = null;
    for (const raw of lines) {
        const line = raw.trimEnd();
        const m = line.match(/^Q(\d+)\s*:\s*(.*)$/);
        if (m) {
            if (cur) qBlocks.push(cur);
            cur = { idx: Number(m[1]), title: m[2] || '', options: [], listOptions: [] };
        } else if (/^\s*-\s+/.test(line)) {
            if (!cur) continue;
            cur.options.push(line.replace(/^\s*-\s+/, ''));
        } else if (/^\s*\+\s+/.test(line)) {
            if (!cur) continue;
            cur.listOptions.push(line.replace(/^\s*\+\s+/, ''));
        } else {
            // ignore unrelated lines
        }
    }
    if (cur) qBlocks.push(cur);

    // Validate count
    if (qBlocks.length !== original.length) {
        throw new Error(`Liczba pytań nie zgadza się: TXT=${qBlocks.length} vs JSON=${original.length}`);
    }

    // Apply by position
    const applyLabelsToOptions = (jsonOpts, labels) => {
        if (!Array.isArray(jsonOpts) || !Array.isArray(labels) || jsonOpts.length !== labels.length) return jsonOpts;
        return jsonOpts.map((opt, idx) => {
            if (typeof opt === 'object' && opt !== null) {
                return { ...opt, label: labels[idx] };
            }
            return labels[idx];
        });
    };

    const updated = original.map((q, i) => {
        const blk = qBlocks[i];
        if (!blk) return q;
        const next = { ...q };
        next.questionContent = blk.title;
        // Apply options by type; keep values, update labels only
        const t = q?.questionType;
        const jsonOpts = Array.isArray(q?.details?.options) ? q.details.options : [];
        const shouldApplyOpts = (t === 'SC' || t === 'MC' || t === 'Open' || t === 'SelectText' || t === 'OpenList');
        if (shouldApplyOpts && blk.options && blk.options.length && blk.options.length === jsonOpts.length) {
            const patched = applyLabelsToOptions(jsonOpts, blk.options);
            next.details = { ...(q.details || {}), options: patched };
        }

        if (t === 'OpenList' && blk.listOptions && blk.listOptions.length) {
            const jsonSelect = Array.isArray(q?.details?.selectOptions) ? q.details.selectOptions : [];
            if (blk.listOptions.length === jsonSelect.length) {
                const patchedSelect = applyLabelsToOptions(jsonSelect, blk.listOptions);
                next.details = { ...(next.details || q.details || {}), selectOptions: patchedSelect };
            }
        }
        return next;
    });

    const result = { ...survey };
    result[lang] = { ...(survey?.[lang] || {}), questionnaire: updated };
    return result;
}
